import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, MapPin, Users } from "lucide-react";
import { PageHero } from "@/components/site/PageBits";
import { BeginnerPricing } from "@/components/site/BeginnerPricing";
import { RetreatDates } from "@/components/site/RetreatDates";
import { BookCallButton } from "@/components/site/BookCall";
import { ShareCourse } from "@/components/site/ShareArticle";
import { useSeo, pageSeo } from "@/lib/seo";
import SEO_PAGES from "@/lib/seo-pages.json";

const SEO = pageSeo("/retreats");
const FAQ = SEO_PAGES.pages["/retreats"].faq;

const WHY = [
  [MapPin, "Hosted at Esc Digital Nomad Village", "Coworking, pool, gym, yoga and the Popoyo surf break on your doorstep, on Nicaragua's Pacific coast."],
  [Users, "Small groups, real attention", "Max 10 people per group (the pilot group is 5), so Greg works with each student individually to understand their needs and create opportunities together."],
  [CalendarDays, "8 nights, Sunday to Monday", "Check in Sunday from 2:00 pm, learn Monday to Sunday, check out Monday morning. Or stay the whole month at Esc."],
];

function Why() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-12 grid md:grid-cols-3 gap-5" data-testid="retreats-why">
      {WHY.map(([Icon, t, d]) => (
        <div key={t} className="rounded-2xl bg-white border border-[#0B2A4A]/10 p-6">
          <Icon className="h-6 w-6 text-[#F08A3C]" />
          <h2 className="mt-3 text-lg font-semibold text-[#0B2A4A]">{t}</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d}</p>
        </div>
      ))}
    </section>
  );
}

function Faq() {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-8 py-14" data-testid="retreats-faq">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Retreat questions</h2>
      <dl className="mt-6 flex flex-col divide-y divide-[#0B2A4A]/10">
        {FAQ.map((f) => <div key={f.q} className="py-4"><dt className="font-semibold text-[#0B2A4A]">{f.q}</dt><dd className="mt-1 text-sm text-slate-600 leading-relaxed">{f.a}</dd></div>)}
      </dl>
      <div className="mt-8 flex flex-wrap gap-3">
        <BookCallButton source="retreats-faq" testId="retreats-faq-book-call-btn" />
        <Link to="/digital-nomad-beginners-course" className="inline-flex items-center gap-2 rounded-xl border border-[#0B2A4A]/20 px-5 py-3 font-semibold text-[#0B2A4A] hover:bg-white" data-testid="retreats-course-link">See the full 7-day curriculum <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}

export default function RetreatsPage() {
  useSeo(SEO);
  return (
    <article data-testid="retreats-page">
      <PageHero eyebrow="RETREATS · Esc DIGITAL NOMAD VILLAGE · POPOYO" title="Digital Nomad Retreats in Popoyo, Nicaragua"
        subtitle="Spend 7 days (8 nights) or a whole month at Esc Digital Nomad Village with Greg Miceli: explore every realistic way to earn online, choose one, and start building it in a small group."
        image="/brand/beginners-course-og.jpg" imageAlt="Sunset aerial view of Esc Digital Nomad Village in Popoyo, Nicaragua">
        <BookCallButton source="retreats-hero" light testId="retreats-hero-book-call-btn" />
        <ShareCourse path="/retreats" title="Digital Nomad Retreats at Esc Digital Nomad Village, Popoyo Nicaragua" />
      </PageHero>
      <Why />
      <RetreatDates />
      <BeginnerPricing />
      <Faq />
    </article>
  );
}
